import React from "react";
import {
  Box,
  Button,
  Badge,
  Heading,
  SimpleGrid,
  Spinner,
  Stack,
  Text,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import OnboardingLayout from "../components/onboarding/OnboardingLayout";

const providers = [
  {
    id: "jobber",
    name: "Jobber",
    description: "Connect your Jobber account to sync customers, workers, jobs, and schedule items.",
    href: "/jobber/oauth/authorize?onboarding=1",
  },
  {
    id: "service_m8",
    name: "ServiceM8",
    description: "Connect your ServiceM8 account to sync customers, staff, jobs, and scheduled work.",
    href: "/oauth/service_m8/start?onboarding=1",
  },
];

export default function OnboardingConnectJobber() {
  const navigate = useNavigate();
  const [selectedProviderId, setSelectedProviderId] = React.useState(null);
  const [loadingConnection, setLoadingConnection] = React.useState(true);
  const [connectedProvider, setConnectedProvider] = React.useState(null);
  const selectedProvider = providers.find((provider) => provider.id === selectedProviderId);
  const connectedProviderLabel = connectedProvider === "service_m8" ? "ServiceM8" : connectedProvider === "jobber" ? "Jobber" : "provider";

  React.useEffect(() => {
    let active = true;

    const fetchCurrentUser = async () => {
      try {
        const response = await fetch("/api/current_user");
        const data = await response.json().catch(() => null);
        if (!active) return;

        if (response.ok && data?.user?.provider_connected) {
          setConnectedProvider(data.user.connected_provider || (data.user.service_m8_connected ? "service_m8" : "jobber"));
        }
      } finally {
        if (active) setLoadingConnection(false);
      }
    };

    fetchCurrentUser();

    return () => {
      active = false;
    };
  }, []);

  const handleConnect = () => {
    if (!selectedProvider) return;

    window.location.assign(selectedProvider.href);
  };

  return (
    <OnboardingLayout
      title="Connect Your Scheduling System"
      subtitle="Choose the system your team uses today. We’ll use its schedule data to find the best spots for same day bookings."
    >
      <Stack spacing={6}>
        <Box border="1px solid" borderColor="gray.200" borderRadius="lg" bg="gray.50" p={6}>
          <Heading size="sm" color="gray.900">
            Our Promise to You
          </Heading>
          <Text mt={3} fontSize="sm" color="gray.600" lineHeight="1.6">
            We promise to never contact anyone without your express permission. We promise to
            never share or sell your data. We promise to keep your data secure behind top of
            the line encryption.
          </Text>
        </Box>

        {loadingConnection ? (
          <Box display="flex" alignItems="center" gap={3} color="gray.600">
            <Spinner size="sm" />
            <Text fontSize="sm">Checking provider connection...</Text>
          </Box>
        ) : connectedProvider ? (
          <Box border="1px solid" borderColor="green.200" borderRadius="lg" bg="green.50" p={5}>
            <Badge colorScheme="green" mb={3}>Connected</Badge>
            <Heading size="sm" color="gray.900" mb={2}>
              {connectedProviderLabel} is connected
            </Heading>
            <Text fontSize="sm" color="gray.700" mb={4}>
              Provider selection is locked for this account. Contact support if you need to move this business to a different provider.
            </Text>
            <Button
              colorScheme="blue"
              onClick={() => navigate(connectedProvider === "service_m8" ? "/onboarding/waitlist/customize" : "/onboarding/waitlist")}
            >
              Continue
            </Button>
          </Box>
        ) : (
          <>
            <Box>
              <Heading size="sm" color="gray.900" mb={3}>
                Select your provider
              </Heading>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
                {providers.map((provider) => {
                  const selected = selectedProviderId === provider.id;

                  return (
                    <Button
                      key={provider.id}
                      type="button"
                      aria-label={`Select ${provider.name}`}
                      variant="outline"
                      display="block"
                      w="100%"
                      h="auto"
                      whiteSpace="normal"
                      textAlign="left"
                      border="1px solid"
                      borderColor={selected ? "blue.500" : "gray.200"}
                      bg={selected ? "blue.50" : "white"}
                      borderRadius="lg"
                      p={5}
                      minH="126px"
                      onClick={() => setSelectedProviderId(provider.id)}
                      _hover={{ borderColor: selected ? "blue.500" : "gray.300", bg: selected ? "blue.50" : "gray.50" }}
                      _focusVisible={{ boxShadow: "0 0 0 3px rgba(49, 130, 206, 0.35)" }}
                    >
                      <Text fontWeight="800" color="gray.900">
                        {provider.name}
                      </Text>
                      <Text mt={2} fontSize="sm" color="gray.600" lineHeight="1.5">
                        {provider.description}
                      </Text>
                    </Button>
                  );
                })}
              </SimpleGrid>
            </Box>

            <Button
              type="button"
              onClick={handleConnect}
              isDisabled={!selectedProvider}
              size="md"
              borderRadius="md"
              colorScheme="blue"
              fontWeight="700"
              alignSelf="flex-start"
            >
              {selectedProvider ? `Connect ${selectedProvider.name}` : "Choose a provider to continue"}
            </Button>
          </>
        )}
      </Stack>
    </OnboardingLayout>
  );
}
